// Types
import { ErrorWithMessage } from './getErrorMessage.type';

/**
 * Checks if the provided error object has a 'message' property and is of type ErrorWithMessage.
 * @param {unknown} error - The error object or value to check.
 * @returns {boolean} True if the error is of type ErrorWithMessage, false otherwise.
 */
const isErrorWithMessage = (error: unknown): error is ErrorWithMessage =>
  typeof error === 'object' &&
  error !== null &&
  'message' in error &&
  typeof (error as Record<string, unknown>).message === 'string';

/**
 * Converts the provided value to an ErrorWithMessage object.
 * If the value is already an ErrorWithMessage, it is returned as is.
 * If the value is an object with a 'message' property, it is wrapped in an ErrorWithMessage.
 * If the value is not an object or does not have a 'message' property, it is converted to a string and wrapped in an ErrorWithMessage.
 * @param {unknown} maybeError - The value to convert to an ErrorWithMessage object.
 * @returns {ErrorWithMessage} The converted ErrorWithMessage object.
 */
const toErrorWithMessage = (maybeError: unknown): ErrorWithMessage => {
  if (isErrorWithMessage(maybeError)) return maybeError;
  try {
    return new Error(JSON.stringify(maybeError));
  } catch {
    return new Error(String(maybeError));
  }
};

/**
 * Retrieves the error message from the provided error object.
 * If the error object is an ErrorWithMessage, the message property is returned.
 * If the error object is not an ErrorWithMessage, it is converted to an ErrorWithMessage and its message property is returned.
 * @param {unknown} error - The error object to retrieve the message from.
 * @returns {string} The error message.
 */
export const getErrorMessage = (error: unknown) => toErrorWithMessage(error).message;
